import { ChevronDown, Search, X } from "lucide-react";
import { useId, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils/cn";

/** Small status pill (tones map to design tokens; all ≥4.5:1 contrast). */
export function Pill({
  children,
  tone = "neutral",
}: {
  children: ReactNode;
  tone?: "neutral" | "success" | "warning" | "info";
}) {
  const tones: Record<string, string> = {
    neutral: "bg-secondary text-secondary-foreground",
    success: "bg-success text-success-foreground",
    warning: "bg-warning text-warning-foreground",
    info: "bg-accent text-accent-foreground",
  };
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium",
        tones[tone],
      )}
    >
      {children}
    </span>
  );
}

/**
 * A collapsible "advanced search" block for the secondary filter controls.
 *
 * The reports filter bar had grown to eight controls in one grid, which is the
 * view the ticket asks to lighten: the everyday controls stay visible and the
 * rest fold away. Starts open when something inside it is set, so a filter is
 * never in force behind a closed section — and the active-filter chips are
 * deliberately rendered *outside* this block for the same reason.
 */
export function AdvancedFilters({
  activeCount,
  children,
}: {
  activeCount: number;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <details className="group rounded-lg border border-border" open={activeCount > 0}>
      <summary className="flex min-h-touch cursor-pointer list-none items-center gap-2 px-3 text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
        <ChevronDown
          className="size-4 shrink-0 transition-transform group-open:rotate-180"
          aria-hidden
        />
        {t("reports.filters.advanced")}
        {activeCount > 0 && (
          <span className="rounded-full bg-primary px-1.5 py-0.5 text-[11px] font-semibold text-primary-foreground">
            {activeCount}
          </span>
        )}
      </summary>
      <div className="border-t border-border p-3">{children}</div>
    </details>
  );
}

/**
 * One filter control with its label above it. Every control in the report
 * filter grids goes through here, so each cell has the same label line and a
 * row lines up — the Django report forms put the same label over each of
 * theirs (`filters/labelled-filter.html`).
 *
 * `children` receives the id to put on the control. The label's own id is
 * `${id}-label`, for the multi-select trigger, which names itself with
 * `aria-labelledby`.
 */
export function FilterField({
  label,
  error,
  children,
}: {
  label: string;
  /** Shown under the control, as the Django forms show a field's error. */
  error?: string;
  children: (id: string) => ReactNode;
}) {
  const id = useId();
  return (
    <div className="min-w-0 space-y-1.5">
      <Label id={`${id}-label`} htmlFor={id} className="text-xs text-muted-foreground">
        {label}
      </Label>
      {children(id)}
      {error && (
        <p role="alert" className="text-xs text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}

/** Shared trigger styling for the compact filter-bar controls. */
export const filterControlClass =
  "flex min-h-touch items-center gap-2 rounded-lg border border-input bg-background px-3 text-sm " +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

const ISO_MONTH = /^(\d{4})-(0[1-9]|1[0-2])$/;
const DJANGO_MONTH = /^(0[1-9]|1[0-2])\/(\d{4})$/;

/** `YYYY-MM` for a complete month typed either way (`2026-01`, `01/2026`). */
export function toIsoMonth(text: string): string | undefined {
  if (ISO_MONTH.test(text)) return text;
  const django = DJANGO_MONTH.exec(text);
  return django ? `${django[2]}-${django[1]}` : undefined;
}

/**
 * A month picker for the report date ranges, which run by month as on the
 * Django pages. Browsers without a month picker (desktop Firefox and Safari)
 * render a text box instead, so it carries the Django page's « mm/aaaa » hint
 * and hands on only a complete month — never the half-typed text, which the
 * API would refuse.
 */
export function MonthInput({
  id,
  value,
  onChange,
}: {
  id: string;
  value: string | undefined;
  onChange: (value: string | undefined) => void;
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(value ?? "");
  // Follow a change made elsewhere (a chip removed); text still being typed
  // has handed nothing on, so it is left alone.
  const committed = draft ? toIsoMonth(draft) : undefined;
  if (committed !== value && (draft === "" || committed !== undefined)) {
    setDraft(value ?? "");
  }
  return (
    <input
      id={id}
      type="month"
      value={draft}
      placeholder={t("reports.filters.monthPlaceholder")}
      pattern="\d{4}-\d{2}|\d{2}/\d{4}"
      onChange={(e) => {
        const text = e.target.value;
        setDraft(text);
        if (!text) onChange(undefined);
        else if (toIsoMonth(text)) onChange(toIsoMonth(text));
      }}
      className={cn(filterControlClass, "w-full text-muted-foreground")}
    />
  );
}

/**
 * A search box + inline filter controls laid out in one wrapping row (the
 * compact filter bar shared by the enterprise list, progress, and search).
 */
export function FilterBar({ children }: { children: ReactNode }) {
  return <div className="flex flex-wrap items-center gap-2">{children}</div>;
}

/** Icon search input sized for the filter bar. */
export function SearchInput({
  id,
  value,
  onChange,
  placeholder,
}: {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}) {
  return (
    <div className="relative min-w-56 flex-1">
      <Search
        className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
        aria-hidden
      />
      <input
        id={id}
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={cn(
          "min-h-touch w-full rounded-lg border border-input bg-background pl-9 pr-3 text-sm",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        )}
      />
    </div>
  );
}

/**
 * A dropdown multi-select for the filter bar: a compact trigger showing the
 * selected count, opening a searchable checkbox panel. Replaces the tall inline
 * checkbox boxes. Closes on outside click via a transparent backdrop.
 *
 * Its visible label sits above it (`FilterField`). The trigger shows
 * `placeholder` (« Tous les enjeux ») until something is picked, and is named
 * by the label plus that summary through `aria-labelledby`.
 */
export function MultiSelectFilter({
  id,
  placeholder,
  options,
  selected,
  onToggle,
}: {
  id: string;
  placeholder: string;
  options: { id: string; name: string }[];
  selected: string[];
  onToggle: (id: string) => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? options.filter((o) => o.name.toLowerCase().includes(q)) : options;
  }, [options, query]);

  return (
    <div className="relative w-full">
      <button
        id={id}
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-labelledby={`${id}-label ${id}-summary`}
        className={cn(
          filterControlClass,
          // Fill the grid cell like the sibling <select>s so the filter grid
          // reads as an even row instead of ragged content-width buttons.
          "w-full",
          selected.length > 0 && "border-primary/40 bg-primary/5",
        )}
      >
        <span id={`${id}-summary`} className="truncate">
          {placeholder}
        </span>
        {selected.length > 0 && (
          <span className="rounded-full bg-primary px-1.5 text-xs font-bold text-primary-foreground">
            {selected.length}
          </span>
        )}
        <ChevronDown className="ml-auto size-4 shrink-0 text-muted-foreground" aria-hidden />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute left-0 z-20 mt-1 w-64 rounded-lg border border-border bg-popover p-2 shadow-lg">
            {options.length > 8 && (
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t("reports.filters.search")}
                className="mb-2 w-full rounded-md border border-input bg-background px-2.5 py-1.5 text-sm"
              />
            )}
            <div className="max-h-60 space-y-0.5 overflow-y-auto pr-1">
              {filtered.length === 0 ? (
                <p className="px-1 py-2 text-sm text-muted-foreground">
                  {t("reports.filters.noMatch")}
                </p>
              ) : (
                filtered.map((option) => (
                  <label
                    key={option.id}
                    className="flex cursor-pointer items-center gap-2.5 rounded px-1 py-1 text-sm hover:bg-accent"
                  >
                    <input
                      type="checkbox"
                      className="size-4 shrink-0 rounded border-input text-primary focus-visible:ring-2 focus-visible:ring-ring"
                      checked={selected.includes(option.id)}
                      onChange={() => onToggle(option.id)}
                    />
                    <span className="min-w-0 flex-1">{option.name}</span>
                  </label>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/** A removable active-filter chip. */
export function FilterChip({
  label,
  onRemove,
}: {
  label: ReactNode;
  onRemove: () => void;
}) {
  const { t } = useTranslation();
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-muted py-1 pl-3 pr-1 text-xs font-medium text-foreground">
      {label}
      <button
        type="button"
        onClick={onRemove}
        className="grid size-4 place-items-center rounded-full text-muted-foreground hover:bg-border hover:text-foreground"
        aria-label={t("reports.filters.removeFilter")}
      >
        <X className="size-3" aria-hidden />
      </button>
    </span>
  );
}

